export interface GroupSummary {
  values: Record<string, unknown>
  count: number
  sums: Record<string, number>
}

export type GroupPath = Array<{ field: string; value: unknown }>

export type FlatEntry =
  | {
      kind: 'group'
      path: GroupPath
      label: unknown
      count: number
      sums: Record<string, number>
      depth: number
      expanded: boolean
      isLeafLevel: boolean
    }
  | { kind: 'row'; groupPath: GroupPath; rowIndex: number }

/**
 * Pure state model for a 1C-style lazy group tree. Holds group summaries
 * per level (loaded on demand from the backend) and expansion state, and
 * flattens the visible entries depth-first for a virtualizer.
 *
 * Caller contract: an instance is valid for ONE query (levels + filters +
 * search). On any query change, construct a fresh model — `setGroups` does
 * not prune expansion/children state for group values that vanished from a
 * reloaded level, so reusing an instance across refetches can surface stale
 * children for a re-appearing group key.
 */
export class GroupTreeModel {
  private readonly childrenByKey = new Map<string, GroupSummary[]>()
  private readonly expanded = new Set<string>()

  constructor(private readonly levels: string[]) {}

  keyOf(path: GroupPath): string {
    return JSON.stringify(path.map((p) => [p.field, typeof p.value, p.value]))
  }

  setGroups(path: GroupPath, groups: GroupSummary[]): void {
    this.childrenByKey.set(this.keyOf(path), groups)
  }

  isExpanded(path: GroupPath): boolean {
    return this.expanded.has(this.keyOf(path))
  }

  toggle(path: GroupPath): void {
    const key = this.keyOf(path)
    if (this.expanded.has(key)) {
      this.expanded.delete(key)
    } else {
      this.expanded.add(key)
    }
  }

  needsChildren(path: GroupPath): boolean {
    // path identifies a single group; its own level index is path.length - 1.
    // Children (if any) live one level deeper, at index path.length.
    const isLeafLevel = path.length >= this.levels.length
    if (isLeafLevel) return false
    if (!this.isExpanded(path)) return false
    return !this.childrenByKey.has(this.keyOf(path))
  }

  flatten(): FlatEntry[] {
    const out: FlatEntry[] = []
    this.flattenLevel([], out)
    return out
  }

  private flattenLevel(path: GroupPath, out: FlatEntry[]): void {
    const depth = path.length
    const groups = this.childrenByKey.get(this.keyOf(path))
    if (!groups) return
    const field = this.levels[depth]
    const isLeafLevel = depth === this.levels.length - 1
    for (const group of groups) {
      const childPath: GroupPath = [...path, { field, value: group.values[field] }]
      const expanded = this.isExpanded(childPath)
      out.push({
        kind: 'group',
        path: childPath,
        label: group.values[field],
        count: group.count,
        sums: group.sums,
        depth,
        expanded,
        isLeafLevel,
      })
      if (!expanded) continue
      if (isLeafLevel) {
        for (let rowIndex = 0; rowIndex < group.count; rowIndex += 1) {
          out.push({ kind: 'row', groupPath: childPath, rowIndex })
        }
      } else {
        this.flattenLevel(childPath, out)
      }
    }
  }
}
